// Canonical display names for marketplace platforms. Accepts either a raw platform key
// (e.g. 'ebay') or an account-suffixed variant from a listing's listedOn (e.g. 'vintedDe',
// 'shopify_2'), so every dialog renders the same label for the same marketplace.
const PLATFORM_DISPLAY_NAMES = {
  ebay: 'eBay',
  etsy: 'Etsy',
  shopify: 'Shopify',
  wooCommerce: 'WooCommerce',
  bigCommerce: 'BigCommerce',
  depop: 'Depop',
  grailed: 'Grailed',
  whatnot: 'Whatnot',
  offerup: 'OfferUp',
  vestiaire: 'Vestiaire Collective',
  poshmark: 'Poshmark',
  mercari: 'Mercari',
};

// True when a platform key points at Shopify. A listing's listedOn can carry an account-suffixed
// key ('shopify_2') alongside the plain one, so the marketplace is matched on the name it contains
// rather than on an exact value.
export function isShopifyPlatform (platform) {
  return !!platform && platform.includes('shopify');
}

export function getPlatformDisplayName (platform) {
  if (platform?.startsWith('vinted')) return 'Vinted';
  if (isShopifyPlatform(platform)) return 'Shopify';
  return PLATFORM_DISPLAY_NAMES[platform] || platform;
}

// The integration statuses that mean Voolist can no longer manage an account's placements:
// the token died on its own ('expired') or the user / a plan cancel removed the account
// ('disconnected'). A placement stays 'active' through either event (a placement is a fact),
// so the integration's status is the only "can we still manage this?" signal.
export const DISCONNECTED_INTEGRATION_STATUSES = ['disconnected', 'expired'];

// True when an integration status means the account is unmanageable (see the constant above).
export function isDisconnectedStatus (integrationStatus) {
  return DISCONNECTED_INTEGRATION_STATUSES.includes(integrationStatus);
}

// Wording for the listing edit form's non-editable "reconnect" hint. Both cases get the same
// greyed treatment + Reconnect link — only the reason wording differs:
//   'expired'      → the token expired on its own    → "Connection lost"
//   'disconnected' → the user removed the account     → "Disconnected"
// Any other / unknown value falls back to the generic "Disconnected".
export function getReconnectHintLabel (integrationStatus) {
  return integrationStatus === 'expired' ? 'Connection lost' : 'Disconnected';
}

// Decide whether a platform's badge in the listing table should render greyed (disconnected).
// A platform greys out only when Voolist can't manage it for this listing — i.e. it has at
// least one live placement and EVERY live placement sits on a disconnected/expired account.
// If any live placement is on an active account, or there are no placements to judge (e.g. a
// legacy scalar-only listing with no placement rows), the badge stays normal.
export function isPlatformDisconnectedOnly (platform, placements) {
  const live = (placements || []).filter(
    (pa) => pa.platform === platform && pa.status === 'active'
  );
  if (live.length === 0) return false;
  return live.every((pa) => isDisconnectedStatus(pa.integration?.status));
}
